import { useState, useEffect } from 'react';
import { FiAward, FiGift, FiClock, FiCalendar, FiUser, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BaseUrl } from "../common/BaseUrl";

const EmployeeRewardDashboard = () => {
    const employeeId = localStorage.getItem("_id");
    const [activeTab, setActiveTab] = useState('earned');
    const [rewards, setRewards] = useState({
        earned: [],
        redeemed: [],
        donations: [],
        pending: [],
        rejected: []
    });
    const [stats, setStats] = useState({
        earned: 0,
        available: 0,
        redeemed: 0,
        donations: 0,
        pending: 0,
        rejected: 0
    });
    const [loading, setLoading] = useState(true);
    const [colleagues, setColleagues] = useState([]);
    const [donationForm, setDonationForm] = useState({
        recipientId: '',
        rewardId: '',
        hours: 0.5,
        message: ''
    });
    const [vacationForm, setVacationForm] = useState({
        rewardId: '',
        hours: 0.5,
        startDate: '',
        endDate: '',
        notes: ''
    });

    const api = axios.create({
        baseURL: BaseUrl,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [rewardsRes, colleaguesRes] = await Promise.all([
                    api.get(`/rewards/employee/${employeeId}`),
                    api.get('/emp/colleagues')
                ]);
                
                // Calculate available hours for each reward
                const processedEarned = rewardsRes.data.earned?.map(reward => {
                    const pendingRequests = rewardsRes.data.pending?.filter(req => req.rewardId === reward._id) || [];
                    const redeemedRequests = rewardsRes.data.redeemed?.filter(req => req.rewardId === reward._id) || [];
                    
                    const pendingHours = pendingRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                    const redeemedHours = redeemedRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                    
                    return {
                        ...reward,
                        pendingHours,
                        redeemedHours,
                        availableHours: reward.bonusHours - pendingHours - redeemedHours
                    };
                }) || [];

                setRewards({
                    earned: processedEarned.filter(r => r.status !== 'redeemed'),
                    redeemed: rewardsRes.data.redemptions || [],
                    donations: rewardsRes.data.donations || [],
                    pending: rewardsRes.data.pending || [],
                    rejected: rewardsRes.data.rejected || [],
                });
                setStats({
                    earned: rewardsRes.data.totals.earned,
                    redeemed: rewardsRes.data.totals.redeemed || 0,
                    available: rewardsRes.data.totals.available || 0,

                    donations: rewardsRes.data.totals.donations || 0,
                    pending: rewardsRes.data.totals.pending || 0,
                    rejected: rewardsRes.data.totals.rejected || 0,
                })
                setColleagues(colleaguesRes.data.result || []);
                setLoading(false);
            } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to load data');
                setLoading(false);
            }
        };

        fetchData();
    }, [employeeId]);

    const handleVacationRequest = async () => {
        try {
            if (!vacationForm.startDate || !vacationForm.endDate) {
                toast.error('Please select start and end dates');
                return;
            }

            if (new Date(vacationForm.startDate) > new Date(vacationForm.endDate)) {
                toast.error('End date must be after start date');
                return;
            }

            if (vacationForm.hours <= 0) {
                toast.error('Please enter valid hours');
                return;
            }

            await api.post(`/rewards/${vacationForm.rewardId}/request-vacation`, vacationForm);
            toast.success('Vacation request submitted for approval!');
            
            // Refresh rewards
            const { data } = await api.get(`/rewards/employee/${employeeId}`);
            const processedEarned = data.earned?.map(reward => {
                const pendingRequests = data.pending?.filter(req => req.rewardId === reward._id) || [];
                const redeemedRequests = data.redeemed?.filter(req => req.rewardId === reward._id) || [];
                
                const pendingHours = pendingRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                const redeemedHours = redeemedRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                
                return {
                    ...reward,
                    pendingHours,
                    redeemedHours,
                    availableHours: reward.bonusHours - pendingHours - redeemedHours
                };
            }) || [];

            setRewards({
                earned: processedEarned.filter(r => r.status !== 'redeemed'),
                redeemed: data.redemptions || [],
                donations: data.donations || [],
                pending: data.pending || [],
                rejected: data.rejected || []
            });
            setStats({
                earned: data.totals.redeemed,
                redeemed: data.totals.redeemed || 0,
                available: data.totals.available || 0,
                donations: data.totals.donations || 0,
                pending: data.totals.pending || 0,
                rejected: data.totals.rejected || 0,
            })

            setVacationForm({
                rewardId: '',
                hours: 0.5,
                startDate: '',
                endDate: '',
                notes: ''
            });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Request failed');
        }
    };

    const handleDonationRequest = async () => {
        try {
            if (!donationForm.recipientId) {
                toast.error('Please select a recipient');
                return;
            }

            if (donationForm.hours <= 0) {
                toast.error('Please enter valid hours');
                return;
            }

            await api.post(`/rewards/${donationForm.rewardId}/request-donation`, donationForm);
            toast.success('Donation request submitted for approval!');
            
            // Refresh rewards and reset form
            const { data } = await api.get(`/rewards/employee/${employeeId}`);
            const processedEarned = data.earned?.map(reward => {
                const pendingRequests = data.pending?.filter(req => req.rewardId === reward._id) || [];
                const redeemedRequests = data.redeemed?.filter(req => req.rewardId === reward._id) || [];
                
                const pendingHours = pendingRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                const redeemedHours = redeemedRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                
                return {
                    ...reward,
                    pendingHours,
                    redeemedHours,
                    availableHours: reward.bonusHours - pendingHours - redeemedHours
                };
            }) || [];

            setRewards({
                earned: processedEarned.filter(r => r.status !== 'redeemed'),
                redeemed: data.redemptions || [],
                donations: data.donations || [],
                pending: data.pending || [],
                rejected: data.rejected || []
            });
            setDonationForm({
                recipientId: '',
                rewardId: '',
                hours: 0.5,
                message: ''
            });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Request failed');
        }
    };

    const updateDonationForm = (field, value) => {
        setDonationForm(prev => ({
            ...prev,
            [field]: value
        }));

        // When reward is selected, set max hours to its availableHours
        if (field === 'rewardId') {
            const selectedReward = rewards.earned.find(r => r._id === value);
            if (selectedReward) {
                setDonationForm(prev => ({
                    ...prev,
                    hours: Math.min(prev.hours, selectedReward.availableHours),
                    maxHours: selectedReward.availableHours
                }));
            }
        }
    };

    const updateVacationForm = (field, value) => {
        setVacationForm(prev => ({
            ...prev,
            [field]: value
        }));

        // When reward is selected, auto-set reasonable dates and available hours
        if (field === 'rewardId' && value) {
            const selectedReward = rewards.earned.find(r => r._id === value);
            if (selectedReward) {
                const weekEnd = new Date(selectedReward.weekEndDate);
                const defaultStart = new Date(weekEnd);
                defaultStart.setDate(weekEnd.getDate() + 7); // Suggest next week
                
                setVacationForm(prev => ({
                    ...prev,
                    startDate: defaultStart.toISOString().split('T')[0],
                    endDate: new Date(defaultStart.setDate(defaultStart.getDate() + 1)).toISOString().split('T')[0],
                    maxHours: selectedReward.availableHours,
                    hours: Math.min(prev.hours, selectedReward.availableHours)
                }));
            }
        }
    };

    const cancelPendingRequest = async (requestId, type) => {
        try {
            await api.delete(`/rewards/requests/${requestId}`);
            toast.success(`${type === 'donation' ? 'Donation' : 'Vacation'} request cancelled`);
            
            // Refresh data
            const { data } = await api.get(`/rewards/employee/${employeeId}`);
            const processedEarned = data.earned?.map(reward => {
                const pendingRequests = data.pending?.filter(req => req.rewardId === reward._id) || [];
                const redeemedRequests = data.redeemed?.filter(req => req.rewardId === reward._id) || [];
                
                const pendingHours = pendingRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                const redeemedHours = redeemedRequests.reduce((sum, req) => sum + req.hoursRedeemed, 0);
                
                return {
                    ...reward,
                    pendingHours,
                    redeemedHours,
                    availableHours: reward.bonusHours - pendingHours - redeemedHours
                };
            }) || [];

            setRewards({
                earned: processedEarned.filter(r => r.status !== 'redeemed'),
                redeemed: data.redemptions || [],
                donations: data.donations || [],
                pending: data.pending || [],
                rejected: data.rejected || []
            });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Cancellation failed');
        }
    };

    // Filter rewards that have available hours
    const availableRewards = rewards.earned.filter(reward => reward.availableHours > 0);



    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header */}
                <div className="bg-green-600  from-blue-600 to-blue-800 p-3 text-white">
                    <h1 className="text-2xl font-bold flex items-center">
                        <FiAward className="mr-2" /> My Rewards Dashboard
                    </h1>
                    <p className="mt-2 opacity-90">Manage your earned bonus hours and redemptions</p>
                </div>

                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-gray-50 border-b">
                    <div className="bg-white p-3 rounded shadow-sm text-center">
                        <p className="text-sm text-gray-500">Total Earned</p>
                        <p className="text-xl font-bold text-blue-600">{stats?.earned?.toFixed(1)} hrs</p>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm text-center">
                        <p className="text-sm text-gray-500">Available</p>
                        <p className="text-xl font-bold text-green-600">{stats?.available?.toFixed(1)} hrs</p>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm text-center">
                        <p className="text-sm text-gray-500">Pending</p>
                        <p className="text-xl font-bold text-yellow-600">{stats?.pending?.toFixed(1)} hrs</p>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm text-center">
                        <p className="text-sm text-gray-500">Redeemed</p>
                        <p className="text-xl font-bold text-purple-600">{stats?.redeemed?.toFixed(1)} hrs</p>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm text-center">
                        <p className="text-sm text-gray-500">Rejected</p>
                        <p className="text-xl font-bold text-teal-600">0 hrs</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('earned')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'earned' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <FiAward className="inline mr-2" /> Available Rewards
                        </button>
                        <button
                            onClick={() => setActiveTab('redeem')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'redeem' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <FiGift className="inline mr-2" /> Request Redemption
                        </button>
                        <button
                            onClick={() => setActiveTab('pending')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'pending' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <FiAlertCircle className="inline mr-2" /> Pending Requests
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <FiClock className="inline mr-2" /> History
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-4 md:p-6">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <>
                            {/* Available Rewards Tab */}
                            {activeTab === 'earned' && (
                                <div>
                                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                                        <FiAward className="mr-2 text-blue-500" /> Available Bonus Hours
                                    </h2>
                                    
                                    {availableRewards.length === 0  ? (
                                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                                            <p className="text-gray-500">No rewards available for redemption</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {availableRewards.map(reward => (
                                                <div key={reward._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex justify-between items-start" >
                                                        <div>
                                                            <h3 className="font-medium flex items-center">
                                                                <FiClock className="mr-2 text-green-500" />
                                                                {reward.totalWorkedHours.toFixed(1)} hours worked
                                                            </h3>
                                                            <p className="text-sm text-gray-500 mt-1">
                                                                Week of {new Date(reward.weekStartDate).toLocaleDateString()} - {new Date(reward.weekEndDate).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-lg font-semibold text-green-600">
                                                                +{reward.bonusHours.toFixed(1)} bonus hours
                                                            </div>
                                                            <div className="text-sm text-gray-600 mt-1">
                                                                <span className="font-medium">Available:</span> {stats?.available.toFixed(1)} hrs
                                                            </div>
                                                            <div className="flex space-x-2 mt-1">
                                                                {reward.redeemedHours > 0 && (
                                                                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded">
                                                                        Redeemed: {reward.redeemedHours.toFixed(1)} hrs
                                                                    </span>
                                                                )}
                                                                {reward.pendingHours > 0 && (
                                                                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded">
                                                                        Pending: {reward.pendingHours.toFixed(1)} hrs
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Request Redemption Tab */}
                            {activeTab === 'redeem' && (
                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Vacation Request */}
                                    <div>
                                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                                            <FiCalendar className="mr-2 text-blue-500" /> Request Vacation Time
                                        </h2>
                                        
                                        <div className="border rounded-lg p-6 bg-gray-50">
                                            {availableRewards.length > 0 && stats.available > 0 ? (
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Reward</label>
                                                        <select
                                                            value={vacationForm.rewardId}
                                                            onChange={(e) => updateVacationForm('rewardId', e.target.value)}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            required
                                                        >
                                                            <option value="">Choose reward to redeem</option>
                                                            {availableRewards.map(reward => (
                                                                <option key={reward._id} value={reward._id}>
                                                                    {stats.available.toFixed(1)}/{reward.bonusHours.toFixed(1)} hours (Week of {new Date(reward.weekStartDate).toLocaleDateString()})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4"  style={{display :"none"}}>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                                            <input
                                                                type="date"
                                                                value={vacationForm.startDate}
                                                                onChange={(e) => updateVacationForm('startDate', e.target.value)}
                                                                className="w-full border rounded-md p-2 text-sm"
                                                                required
                                                                disabled={!vacationForm.rewardId}
                                                                min={new Date().toISOString().split('T')[0]}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                                            <input
                                                                type="date"
                                                                value={vacationForm.endDate}
                                                                onChange={(e) => updateVacationForm('endDate', e.target.value)}
                                                                className="w-full border rounded-md p-2 text-sm"
                                                                required
                                                                disabled={!vacationForm.rewardId}
                                                                min={vacationForm.startDate || new Date().toISOString().split('T')[0]}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hours to Redeem</label>
                                                        <input
                                                            type="number"
                                                            min="0.5"
                                                            step="0.5"
                                                            max={stats?.available || 0}
                                                            value={vacationForm.hours}
                                                            onChange={(e) => updateVacationForm('hours', parseFloat(e.target.value))}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            required
                                                            disabled={!vacationForm.rewardId}
                                                        />
                                                        {vacationForm.rewardId && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Max available: {stats.available?.toFixed(1) || 0} hours
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                                                        <textarea
                                                            value={vacationForm.notes}
                                                            onChange={(e) => updateVacationForm('notes', e.target.value)}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            rows="2"
                                                            disabled={!vacationForm.rewardId}
                                                        />
                                                    </div>

                                                    <button
                                                        onClick={handleVacationRequest}
                                                        disabled={!vacationForm.rewardId || !vacationForm.startDate || !vacationForm.endDate || stats.available <= 0}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <FiCheck className="mr-2" /> Submit Vacation Request
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                                    <div className="flex">
                                                        <div className="flex-shrink-0">
                                                            <FiAlertCircle className="h-5 w-5 text-yellow-400" />
                                                        </div>
                                                        <div className="ml-3">
                                                            <p className="text-sm text-yellow-700">
                                                                No rewards available for vacation requests. All your rewards are either pending approval or already redeemed.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Donation Request */}
                                    <div>
                                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                                            <FiGift className="mr-2 text-purple-500" /> Request to Donate Hours
                                        </h2>
                                        
                                        <div className="border rounded-lg p-6 bg-gray-50">
                                            {availableRewards.length  > 0 && stats.available > 0 ? (
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Reward</label>
                                                        <select
                                                            value={donationForm.rewardId}
                                                            onChange={(e) => updateDonationForm('rewardId', e.target.value)}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            required
                                                        >
                                                            <option value="">Choose reward to donate</option>
                                                            {availableRewards.map(reward => (
                                                                <option key={reward._id} value={reward._id}>
                                                                    {stats.available.toFixed(1)}/{reward.bonusHours.toFixed(1)} hours (Week of {new Date(reward.weekStartDate).toLocaleDateString()})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
                                                        <select
                                                            value={donationForm.recipientId}
                                                            onChange={(e) => updateDonationForm('recipientId', e.target.value)}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            required
                                                            disabled={!donationForm.rewardId}
                                                        >
                                                            <option value="">Select colleague</option>
                                                            {colleagues.map(colleague => (
                                                                <option key={colleague._id} value={colleague._id}>
                                                                    {colleague.name} ({colleague.position})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hours to Donate</label>
                                                        <input
                                                            type="number"
                                                            min="0.5"
                                                            step="0.5"
                                                            max={stats.available || 0}
                                                            value={donationForm.hours}
                                                            onChange={(e) => updateDonationForm('hours', parseFloat(e.target.value))}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            required
                                                            disabled={!donationForm.rewardId}
                                                        />
                                                        {donationForm.rewardId && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Max available: {stats.available?.toFixed(1) || 0} hours
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                                                        <textarea
                                                            value={donationForm.message}
                                                            onChange={(e) => updateDonationForm('message', e.target.value)}
                                                            className="w-full border rounded-md p-2 text-sm"
                                                            rows="2"
                                                            maxLength="500"
                                                            disabled={!donationForm.rewardId}
                                                        />
                                                    </div>

                                                    <button
                                                        onClick={handleDonationRequest}
                                                        disabled={!donationForm.rewardId || !donationForm.recipientId || stats.available <= 0}
                                                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <FiCheck className="mr-2" /> Submit Donation Request
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                                    <div className="flex">
                                                        <div className="flex-shrink-0">
                                                            <FiAlertCircle className="h-5 w-5 text-yellow-400" />
                                                        </div>
                                                        <div className="ml-3">
                                                            <p className="text-sm text-yellow-700">
                                                                No rewards available for donations. All your rewards are either pending approval or already redeemed.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pending Requests Tab */}
                            {activeTab === 'pending' && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                                        <FiAlertCircle className="mr-2 text-yellow-500" /> Pending Approval
                                    </h2>
                                    
                                    {rewards.pending.length === 0 ? (
                                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                                            <p className="text-gray-500">No pending requests</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {rewards.pending.map(request => {
                                                const reward = rewards.earned.find(r => r._id === request.rewardId) || {};
                                                return (
                                                    <div key={request._id} className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h3 className="font-medium flex items-center">
                                                                    {request.redemptionType === 'vacation' ? (
                                                                        <FiCalendar className="mr-2 text-blue-500" />
                                                                    ) : (
                                                                        <FiGift className="mr-2 text-purple-500" />
                                                                    )}
                                                                    {request.redemptionType === 'vacation' ? 'Vacation Request' : 'Donation Request'}
                                                                </h3>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    {request.hoursRedeemed.toFixed(1)} hours from week of {new Date(reward.weekStartDate).toLocaleDateString()}
                                                                </p>
                                                                {request.redemptionType === 'vacation' && (
                                                                    <p className="text-sm text-gray-600">
                                                                        Dates: {new Date(request.vacationDetails?.startDate).toLocaleDateString()} - {new Date(request.vacationDetails?.endDate).toLocaleDateString()}
                                                                    </p>
                                                                )}
                                                                {request.redemptionType === 'donation' && (
                                                                    <p className="text-sm text-gray-600">
                                                                        To: {request.donationDetails?.recipientName}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col items-end">
                                                                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded mb-2">
                                                                    Pending Approval
                                                                </span>
                                                                {/* <button
                                                                    onClick={() => cancelPendingRequest(request._id, request.redemptionType)}
                                                                    className="text-red-600 hover:text-red-800 text-sm flex items-center"
                                                                >
                                                                    <FiX className="mr-1" /> Cancel Request
                                                                </button> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* History Tab */}
                            {activeTab === 'history' && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                                        <FiClock className="mr-2 text-gray-500" /> Redemption History
                                    </h2>
                                    
                                    {/* Approved Vacation Redemptions */}
                                    {rewards.redeemed.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-medium mb-3 flex items-center">
                                                <FiCalendar className="mr-2 text-blue-500" /> Approved Vacation Time
                                            </h3>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Week</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved On</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {rewards.redeemed.map(request => {
                                                            const reward = rewards.earned.find(r => r._id === request.rewardId) || {};
                                                            return (
                                                                <tr key={request._id}>
                                                                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                                        {new Date(reward.weekStartDate).toLocaleDateString()} - {new Date(reward.weekEndDate).toLocaleDateString()}
                                                                    </td>
                                                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                                                        {request.hoursRedeemed.toFixed(1)} hours
                                                                    </td>
                                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                        {request.vacationDetails?.startDate ? (
                                                                            `${new Date(request.vacationDetails.startDate).toLocaleDateString()} - ${new Date(request.vacationDetails.endDate).toLocaleDateString()}`
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                        {new Date(request.processedAt).toLocaleDateString()}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Donation History */}
                                    {(rewards.donations.length > 0 || rewards.rejected.length > 0) && (
                                        <div>
                                            <h3 className="text-lg font-medium mb-3 flex items-center">
                                                <FiGift className="mr-2 text-purple-500" /> Donation History
                                            </h3>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {[...rewards.donations, ...rewards.rejected].map(donation => (
                                                            <tr key={donation._id}>
                                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {new Date(donation.createdAt).toLocaleDateString()}
                                                                </td>
                                                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                                    {donation.donationDetails?.recipientName || 'Unknown'}
                                                                </td>
                                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                                                                    {donation.hoursRedeemed.toFixed(1)} hours
                                                                </td>
                                                                <td className="px-4 py-4 whitespace-nowrap">
                                                                    {donation.status === 'approved' ? (
                                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                                            Approved
                                                                        </span>
                                                                    ) : donation.status === 'pending' ? (
                                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                                            Pending
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                                            Rejected
                                                                        </span>
                                                                    )}
                                                                    {donation.rejectionReason && (
                                                                        <p className="text-xs text-gray-500 mt-1">{donation.rejectionReason}</p>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {(rewards.redeemed.length === 0 && rewards.donations.length === 0 && rewards.rejected.length === 0) && (
                                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                                            <p className="text-gray-500">No redemption history found</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeRewardDashboard;